import { useState } from "react";
import { Captions, Check, Copy, Loader2, RotateCw } from "lucide-react";

import { Button } from "../ui/button";
import api from "../../services/api";

// O áudio virado texto, dentro da própria bolha da conversa.
//
// O backend já sabia transcrever desde sempre (POST
// /messages/:id/transcribe), mas nada no frontend chamava a rota: na prática o
// recurso não existia para quem atende. É o pedido mais comum de quem recebe
// áudio longo — ler em três segundos o que levaria dois minutos para ouvir, e
// poder ouvir num lugar onde não dá para colocar som.
//
// O texto fica gravado na mensagem (`transcription`), então transcrever é uma
// vez só por áudio: quem abrir a conversa depois já encontra pronto.

// Mensagens do backend traduzidas para o que a pessoa pode fazer a respeito.
// Um "falhou" genérico aqui manda a pessoa perguntar para o suporte o que já
// dava para dizer na hora.
const explicar = (err) => {
	const bruto = String(
		err?.response?.data?.error || err?.response?.data?.message || err?.message || ""
	);

	if (err?.response?.status === 403) {
		return "Seu perfil não tem permissão para usar a IA.";
	}
	if (bruto.includes("ERR_NO_TRANSCRIPTION_PROVIDER")) {
		return "Nenhuma IA configurada para transcrever. Cadastre uma em Inteligência Artificial.";
	}
	if (bruto.includes("ERR_AUDIO_FORMAT_NOT_SUPPORTED")) {
		const ext = bruto.split(":").pop()?.trim();
		return `A IA não lê áudio ${ext || "deste formato"}.`;
	}
	if (bruto.includes("ERR_AUDIO_FILE_NOT_FOUND")) {
		return "O arquivo do áudio não está mais no servidor.";
	}
	if (bruto.includes("ERR_AI_ALL_PROVIDERS_FAILED")) {
		return "Nenhuma IA respondeu. Confira as conexões em Inteligência Artificial.";
	}

	return "Não foi possível transcrever agora. Tente de novo.";
};

const TranscricaoDeAudio = ({ message }) => {
	const [texto, setTexto] = useState(message.transcription || "");
	// Separa "ainda não tentamos" de "tentamos e o áudio não tinha fala" — sem
	// isso o segundo caso viraria um botão que a pessoa aperta para sempre.
	const [tentado, setTentado] = useState(Boolean(message.transcribedAt));
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	const [copiado, setCopiado] = useState(false);

	const transcrever = async (forcar = false) => {
		setCarregando(true);
		setErro("");

		try {
			const { data } = await api.post(
				`/messages/${message.id}/transcribe`,
				{ idioma: "pt", forcar }
			);

			setTexto(data.transcription || "");
			setTentado(true);
		} catch (err) {
			setErro(explicar(err));
		}

		setCarregando(false);
	};

	const copiar = async () => {
		try {
			await navigator.clipboard.writeText(texto);
			setCopiado(true);
			setTimeout(() => setCopiado(false), 1500);
		} catch {
			// Navegador sem permissão de área de transferência: o texto está na
			// tela, dá para selecionar na mão.
		}
	};

	if (!tentado && !texto) {
		return (
			<div className="mt-1">
				<Button
					variant="ghost"
					size="sm"
					className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
					disabled={carregando}
					onClick={() => transcrever(false)}
				>
					{carregando ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<Captions className="h-3 w-3" />
					)}
					{carregando ? "Transcrevendo…" : "Transcrever"}
				</Button>
				{erro && (
					<p className="mt-0.5 text-[11px] leading-snug text-destructive">
						{erro}
					</p>
				)}
			</div>
		);
	}

	return (
		<div className="mt-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
			<div className="mb-1 flex items-center gap-1">
				<Captions className="h-3 w-3 text-muted-foreground" />
				<span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
					Transcrição
				</span>

				<div className="ml-auto flex items-center gap-0.5">
					{texto && (
						<Button
							variant="ghost"
							size="sm"
							className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
							title="Copiar o texto"
							onClick={copiar}
						>
							{copiado ? (
								<Check className="h-3 w-3" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</Button>
					)}
					<Button
						variant="ghost"
						size="sm"
						className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
						title="Transcrever de novo"
						disabled={carregando}
						onClick={() => transcrever(true)}
					>
						{carregando ? (
							<Loader2 className="h-3 w-3 animate-spin" />
						) : (
							<RotateCw className="h-3 w-3" />
						)}
					</Button>
				</div>
			</div>

			{texto ? (
				<p className="whitespace-pre-wrap break-words text-xs leading-relaxed">
					{texto}
				</p>
			) : (
				<p className="text-xs italic text-muted-foreground">
					Nenhuma fala reconhecida neste áudio.
				</p>
			)}

			{erro && (
				<p className="mt-1 text-[11px] leading-snug text-destructive">{erro}</p>
			)}
		</div>
	);
};

export default TranscricaoDeAudio;
